import { REDUCER } from '../reducers/reducersConst';
import * as classroomApi from '../services/api/classroomApi';
import * as drawerActions from '../actions/drawerActions';
import * as messageAlert from './messageAlertAction';
import * as taskActions from '../actions/taskActions';
import { I18n } from 'react-redux-i18n-lite';
import dayjs from 'dayjs';
import * as customTranslateService from '../services/translate/customTranslateService';
import * as submitAssignmentContentPanelService from '../components/drawer-contents/submitAssignmentContentPanelService';
import { getLogger } from '../services/logger';
import { ASSET_TYPES, DRAWER_CONTENTS } from '../const';
import { TASK_TYPE } from '../types/task';
import { findSpecialCharacters, removeDiacritics } from '../utils/stringUtils';

const debug = getLogger('actions:assignmentsDrawerActions');

const handleUploadError = (dispatch, logLabel, err) => {
  dispatch(setUploadContentTaskStatus());
  debug.error('[%s] %s', logLabel, err);
  if (err?.response?.status === 409) {
    dispatch(messageAlert.error(I18n.t('upload.exist')));
  } else if (!err?.response || err.response.status !== 413) {
    dispatch(messageAlert.error(I18n.t('upload.error')));
  }
};

export const setEvaluatedStudentAndOpen = student => {
  return dispatch => {
    dispatch(setEvaluatedStudent(student));
    dispatch(drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.INSTRUCTOR_TASK));
  };
};

export const removeEvaluatedStudentAndOpenStudentsAssignmentPanel = () => {
  return dispatch => {
    dispatch(drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.STUDENTS_ASSIGNMENT));
    dispatch(setEvaluatedStudent());
  };
};

export const setDueDate = (dueDate = dayjs().add(3, 'days')) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_DUE_DATE,
    dueDate
  };
};

export const setStartDate = (startDate = dayjs()) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_START_DATE,
    startDate
  };
};

export const setSelectedGroup = selectedGroup => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_SELECTED_GROUP,
    selectedGroup
  };
};

export const setDescriptionTask = descriptionTask => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_DESCRIPTION_TASK,
    descriptionTask
  };
};

export const setTitleTask = titleTask => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_TITLE_TASK,
    titleTask
  };
};

export const setAllowFile = allowFile => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_ALLOW_FILE,
    allowFile
  };
};

export const setAllowUserCommentResponse = allowUserCommentResponse => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_ALLOW_USER_COMMENT_RESPONSE,
    allowUserCommentResponse
  };
};

export const setEvaluatedStudent = evaluatedStudent => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_EVALUATED_STUDENT,
    evaluatedStudent
  };
};

export const onCheckedActivitiesChange = (checkedActivities = []) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.ON_CHECKED_ACTIVITIES_CHANGE,
    checkedActivities
  };
};

export const setUploadContentTaskStatus = (uploadFileTaskStatus = { percentCompleted: 0, isUploading: false }) => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_UPLOAD_DRAWER_FILE_STATUS,
    uploadFileTaskStatus
  };
};

/**
 * Saves submitted response file in store
 * @param file
 * @param name
 * @returns {{type: string, file: file}}
 */
export const saveFile = (file = null, name = 'file') => {
  return {
    type: REDUCER.ASSIGNMENT_DRAWER.SET_RESPONSE_FILE,
    file: {
      name: name,
      file: file
    }
  };
};

const getTaskName = (customNewName, language) => {
  if (customNewName) return customTranslateService.translateObject(customNewName, language);
  return I18n.t('tasks.newTask', language);
};

export const createAssignment = (assignment, classroomId) => {
  return (dispatch, getState) => {
    const state = getState();
    const language = state.account.user.language;
    const bookId = assignment.book ? assignment.book.id : undefined;
    let name = assignment.name ? assignment.name : getTaskName(assignment.book.name, language);

    let isGroup;
    if (assignment.selectedGroup) {
      isGroup = assignment.selectedGroup.isGroup;
    } else {
      isGroup = false;
    }

    let sectionId;
    if (assignment.selectedGroup) {
      sectionId = assignment.selectedGroup.id === 0 ? assignment.classroom.sectionId : assignment.selectedGroup.id;
    } else {
      sectionId = assignment.classroom.sectionId;
    }

    const students = assignment.students
      ? assignment.students
      : isGroup
      ? []
      : Array.isArray(assignment.selectedGroup.id)
      ? assignment.selectedGroup.id
      : assignment.selectedGroup.users;

    const sectionsForPayload = isGroup ? [sectionId] : [];
    const isAboutContent = assignment.isAboutContent;

    const payload = {
      wording: assignment.wording,
      name,
      startDate: assignment.startDate,
      dueDate: assignment.dueDate,
      activities: assignment.checkedActivities ? assignment.checkedActivities : [],
      sections: sectionsForPayload,
      students,
      bookId: bookId,
      allowFileResponse: assignment.allowFile,
      allowUserCommentResponse: assignment.allowUserComment,
      allowExpiredResponse: assignment.allowExpiredResponse,
      resolutionCriteria: null,
      files: assignment.files
    };

    let createRequest;
    if (isAboutContent) {
      createRequest = classroomApi.createContentTask(payload, classroomId);
    } else if (assignment.taskType === TASK_TYPE.INDIVIDUAL) {
      createRequest = classroomApi.createIndividualTask(payload, classroomId);
    } else {
      createRequest = classroomApi.createGroupTask(payload, classroomId);
    }

    return createRequest.then(res => {
      dispatch(drawerActions.setDrawerClosed());
      dispatch(messageAlert.info(`${I18n.t('tasks.taskCreated')}`));
      return res.data;
    });
  };
};

export const editAssignment = (assignment, classroomId, taskId) => {
  return dispatch => {
    const payload = {
      wording: assignment.wording,
      dueDate: assignment.dueDate,
      startDate: assignment.startDate,
      activities: assignment.checkedActivities ? assignment.checkedActivities : [],
      name: assignment.title,
      allowFileResponse: assignment.allowFile,
      allowUserCommentResponse: assignment.allowUserComment,
      allowExpiredResponse: assignment.allowExpiredResponse,
      files: assignment.files
    };
    return classroomApi.editAssignments(payload, classroomId, taskId).then(res => {
      dispatch(drawerActions.setDrawerClosed());
      dispatch(messageAlert.info(`${I18n.t('tasks.taskUpdated')}`));
      dispatch(taskActions.getClassroomTasksWithResults(classroomId));
      return res.data;
    });
  };
};

export function submitFreeAssignmentResponse(filesToUpload, fileContext) {
  const { userId, classroomId, customerId, taskId } = fileContext;
  return async dispatch => {
    if (!findSpecialCharacters(filesToUpload.map(file => file.name))) {
      dispatch(messageAlert.error(I18n.t('upload.charactersNotAllow')));
      return null;
    }
    const onProgress = status => dispatch(setUploadContentTaskStatus(status));
    const context = { userId, customerId, taskId, assetType: ASSET_TYPES.STUDENT_TASK, classroomId };
    return submitAssignmentContentPanelService
      .uploadFreeAssignmentResponse(filesToUpload, context, onProgress)
      .catch(err => handleUploadError(dispatch, 'submitFreeAssignmentResponse', err));
  };
}

export function submitCommentTaskResponse(commentMessageText, userId, username, customerId, taskId) {
  return async dispatch => {
    return submitAssignmentContentPanelService
      .uploadCommentTaskResponse(commentMessageText, userId, username, customerId, taskId)
      .catch(err => {
        debug.error('[submitCommentTaskResponse] %s', err);
        if (!err?.response || err.response.status !== 413) {
          dispatch(messageAlert.error(I18n.t('upload.error')));
        }
      });
  };
}

export function submitTaskResponse(commentMessageText, filesToUpload, fileContext) {
  const { userId, classroomId, customerId, taskId } = fileContext;
  return async dispatch => {
    if (!findSpecialCharacters(filesToUpload.map(file => file.name))) {
      dispatch(messageAlert.error(I18n.t('upload.charactersNotAllow')));
      return null;
    }
    const onProgress = status => dispatch(setUploadContentTaskStatus(status));
    const context = { userId, customerId, taskId, assetType: ASSET_TYPES.STUDENT_TASK, classroomId };
    return submitAssignmentContentPanelService
      .uploadFreeAssignmentCommentAndFilesResponse(commentMessageText, filesToUpload, context, onProgress)
      .catch(err => handleUploadError(dispatch, 'submitTaskResponse', err));
  };
}

export function submitFreeAssignmentEvaluation(filesToUpload, fileContext) {
  const { customerId, classroomId } = fileContext;
  return async dispatch => {
    if (!findSpecialCharacters(filesToUpload.map(file => file.name))) {
      dispatch(messageAlert.error(I18n.t('upload.charactersNotAllow')));
      return null;
    }
    const onProgress = status => dispatch(setUploadContentTaskStatus(status));
    const context = { customerId, classroomId, assetType: ASSET_TYPES.TEACHER_TASK };
    return submitAssignmentContentPanelService
      .uploadFreeAssignmentEvaluation(filesToUpload, context, onProgress)
      .catch(err => handleUploadError(dispatch, 'submitFreeAssignmentEvaluation', err));
  };
}
